import React, { useState, useEffect } from 'react';
import * as Bixby from '$/core';
import { FINANCIALS_CATEGORY, LENDER_PRESENTATIONS_CATEGORY } from './../company-documents-upload/categories';
import dayjs from 'dayjs';
import './index.scss';

import { FinancialStatementService } from './FinancialStatementService';
import { AgGridReact } from 'ag-grid-react';
import { GridApi, ColumnApi, RowStyle, RowClassParams, ColDef } from 'ag-grid-enterprise';
import './index.scss';
import Loading from '../loading/Loading';

interface ICompanyFinancialsProps {
  currentCompany?: Bixby.Models.Company;
  companyFinancial?: string[][];
  companyDocuments?: Bixby.Models.CompanyDocument[];
  isLoading: boolean;
}

function SourceDocHeader(props) {
  const { displayName, companyDocuments, currentCompany } = props;
  const doesHeaderMatchFYE = (header: string) => header.match(`([0-9]{4}) [({A-Z}{3})]`);

  const doesHeaderMatchQ = (header: string) =>
    header.match(`(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)-(?:[0-3])([0-9]) [(Q)]`);

  const constructDateQuery = (dates: Date[]) => {
    const queryParts: string[] = [];
    dates.map(date => {
      if (dayjs(date).isValid()) {
        queryParts.push(dayjs(date).format('YYYY-MM-') + dayjs(date).daysInMonth());
      }
    });

    return !Bixby.Utils.isNullOrEmpty(queryParts) ? `&date=${queryParts.join('+')}` : '';
  };

  const hasRelatedDocuments = (header: string) => {
    let hasRelatedDocs = false;
    if (companyDocuments) {
      const date = header.split(' ')[0];
      if (doesHeaderMatchQ(header)) {
        const QDocs = companyDocuments.filter(doc =>
          doc.companyDocumentCompanyDocumentCategories.some(
            cat => cat.financialDocumentType == Bixby.Enums.FinancialDocumentType.Q
          )
        );
        if (Bixby.Utils.arrayIsPopulated(QDocs)) {
          const matchingDocs = QDocs.filter(doc =>
            doc.companyDocumentCompanyDocumentCategories.some(
              cat => dayjs(cat.financialDocumentDate).format('MMM-YY') == date
            )
          );
          if (Bixby.Utils.arrayIsPopulated(matchingDocs)) {
            hasRelatedDocs = true;
          }
        }
      }

      if (doesHeaderMatchFYE(header)) {
        const FYEDocs = companyDocuments.filter(doc =>
          doc.companyDocumentCompanyDocumentCategories.some(
            cat => cat.financialDocumentType == Bixby.Enums.FinancialDocumentType.FYE
          )
        );
        if (Bixby.Utils.arrayIsPopulated(FYEDocs)) {
          const matchingDocs = FYEDocs.filter(doc =>
            doc.companyDocumentCompanyDocumentCategories.some(
              cat => dayjs(cat.financialDocumentDate).format('YYYY') == date
            )
          );
          if (Bixby.Utils.arrayIsPopulated(matchingDocs)) {
            hasRelatedDocs = true;
          }
        }
      }
    }

    return hasRelatedDocs;
  };

  const constructDocumentPath = (header: string) => {
    if (currentCompany) {
      //Ensures that link to open in a new tab properly utilizes the CreditSights path prefix
      const conditionalPrefix = Bixby.Configuration.isStandAloneMode ? '/' : Bixby.Configuration.cscpWindowPathPrefix;
      const baseUrlNav = `${conditionalPrefix}company/${currentCompany?.id}/documents`;
      const queryParts: string[] = [`category=${FINANCIALS_CATEGORY}+${LENDER_PRESENTATIONS_CATEGORY}`];
      const date = header.split(' ')[0];

      let hasMatchingFYEDocs = false;
      let hasMatchingQDocs = false;

      if (companyDocuments) {
        if (doesHeaderMatchQ(header)) {
          const QDocs = companyDocuments.filter(doc =>
            doc.companyDocumentCompanyDocumentCategories.some(
              cat => cat.financialDocumentType == Bixby.Enums.FinancialDocumentType.Q
            )
          );
          if (Bixby.Utils.arrayIsPopulated(QDocs)) {
            const matchingDocs = QDocs.filter(doc =>
              doc.companyDocumentCompanyDocumentCategories.some(
                cat => dayjs(cat.financialDocumentDate).format('MMM-YY') == date
              )
            );
            if (Bixby.Utils.arrayIsPopulated(matchingDocs)) {
              hasMatchingQDocs = true;
              queryParts.push(
                constructDateQuery(
                  matchingDocs.flatMap(doc => {
                    const categories = doc.companyDocumentCompanyDocumentCategories
                      .filter(
                        _ =>
                          (_.companyDocumentCategory_Id == FINANCIALS_CATEGORY ||
                            _.companyDocumentCategory_Id == LENDER_PRESENTATIONS_CATEGORY) &&
                          dayjs(_.financialDocumentDate).format('MMM-YY') == date
                      )
                      .map(cat => cat.financialDocumentDate)
                      .filter(_ => !!_);
                    return categories as Date[];
                  })
                )
              );
            }
          }
        }

        if (doesHeaderMatchFYE(header)) {
          const FYEDocs = companyDocuments.filter(doc =>
            doc.companyDocumentCompanyDocumentCategories.some(
              cat => cat.financialDocumentType == Bixby.Enums.FinancialDocumentType.FYE
            )
          );
          if (Bixby.Utils.arrayIsPopulated(FYEDocs)) {
            const matchingDocs = FYEDocs.filter(doc =>
              doc.companyDocumentCompanyDocumentCategories.some(
                cat => dayjs(cat.financialDocumentDate).format('YYYY') == date
              )
            );
            if (Bixby.Utils.arrayIsPopulated(matchingDocs)) {
              hasMatchingFYEDocs = true;
              queryParts.push(
                constructDateQuery(
                  matchingDocs.flatMap(doc => {
                    const categories = doc.companyDocumentCompanyDocumentCategories
                      .filter(
                        _ =>
                          (_.companyDocumentCategory_Id == FINANCIALS_CATEGORY ||
                            _.companyDocumentCategory_Id == LENDER_PRESENTATIONS_CATEGORY) &&
                          dayjs(_.financialDocumentDate).format('YYYY') == date
                      )
                      .map(cat => cat.financialDocumentDate)
                      .filter(_ => !!_);

                    return categories as Date[];
                  })
                )
              );
            }
          }
        }
      }

      queryParts.push(
        hasMatchingFYEDocs && hasMatchingQDocs
          ? '&type=FYE+Q'
          : hasMatchingFYEDocs
          ? '&type=FYE'
          : hasMatchingQDocs
          ? '&type=Q'
          : ''
      );

      const query = queryParts.join('');
      return `${baseUrlNav}?${query}`;
    }
  };

  const value = hasRelatedDocuments(displayName) ? (
    <>
      <div className="indent-header">{displayName}</div>
      <a className="documents_button" href={constructDocumentPath(displayName)} target="_blank">
        Source Docs
      </a>
    </>
  ) : (
    <div>{displayName}</div>
  );
  return value;
}

const FinancialStatement: React.FC<ICompanyFinancialsProps> = props => {
  const { companyFinancial, isLoading, companyDocuments, currentCompany } = props;
  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [gridColumns, setGridColumns] = useState([] as Array<any>);
  const [rowData, setRowData] = useState();

  useEffect(() => {
    if (Bixby.Utils.arrayIsPopulated(companyFinancial)) {
      const firstRowHasStyling =
        companyFinancial?.[1] &&
        !!(companyFinancial?.[0] as any).find((_: string) => _ && _.toLowerCase().indexOf('as of') < 0);
      const first = firstRowHasStyling
        ? companyFinancial?.[1] // first row has column styling, ignore and move to second row
        : companyFinancial?.[0]; // second row doesn't have styling, use first row
      const cols = Object.keys(first as string[]).map((field, index) => {
        const header: string = companyFinancial?.[Bixby.Constants.STATEMENT_HEADER][index] ?? '';
        const cellStyle =
          firstRowHasStyling && companyFinancial?.[0].length > index ? companyFinancial?.[0][index] : undefined;
        const cellClass = FinancialStatementService.generateRowStyle(cellStyle || '', true);
        const col: ColDef = {
          headerName: header,
          field: field,
          cellClass: cellClass ? cellClass.className : undefined,
          hide: cellClass && cellClass.className && cellClass.className.indexOf('hide_row_column') > -1 ? true : false,
          pinned: false,
          minWidth: 140,
          suppressMenu: true,
          resizable: false,
        };
        return col;
      });
      cols.shift();
      cols[0].pinned = true;
      cols[0].minWidth = 280;
      cols[0].cellRenderer = (params: any) => <div title={params.value}>{params.value}</div>;

      const slicedData = companyFinancial?.slice(Bixby.Constants.STATEMENT_SPLICE_ROWS) ?? [];
      const filteredData = slicedData.filter(row => row[0] !== 'hide');

      const rowData: any = [];
      filteredData.forEach(row => {
        const objRow = Object.assign({}, row);
        rowData.push(objRow);
      });

      setRowData(rowData);

      setGridColumns(cols);
    }
  }, [companyDocuments, companyFinancial, currentCompany]);

  const getRowClasses = (params: RowClassParams) => {
    const styles = FinancialStatementService.generateRowStyle(params.data[0]);
    if (styles) {
      return styles.className;
    } else {
      return undefined;
    }
  };

  const getRowStyles: (params: RowClassParams) => RowStyle = (params: RowClassParams) => {
    const styles = FinancialStatementService.generateRowStyle(params.data[0]);
    if (styles) {
      if (styles.styles) {
        return styles.styles;
      }
    }
    return {};
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  return (
    <React.Fragment>
      <section>
        {isLoading && <Loading />}
        {!isLoading && !Bixby.Utils.arrayIsPopulated(companyFinancial) && (
          <p className="space_around">No data available</p>
        )}
        {!isLoading && Bixby.Utils.arrayIsPopulated(companyFinancial) && (
          <div className="financial_statement_data_grid">
            <div className="grid_container">
              <div className="grid ag-theme-alpine">
                <AgGridReact
                  enableRangeSelection={false}
                  defaultColDef={{
                    flex: 1,
                    resizable: true,
                    lockPinned: true,
                    minWidth: 150,
                    cellClass: 'align-left',
                    headerComponentParams: {
                      companyDocuments,
                      currentCompany,
                    },
                    autoHeaderHeight: true,
                  }}
                  onGridReady={onGridReady}
                  rowData={rowData}
                  suppressDragLeaveHidesColumns={true}
                  suppressMovableColumns={true}
                  rowHeight={32}
                  headerHeight={32}
                  getRowStyle={getRowStyles}
                  getRowClass={getRowClasses}
                  columnDefs={gridColumns}
                  components={{ agColumnHeader: SourceDocHeader }}></AgGridReact>
              </div>
            </div>
          </div>
        )}
      </section>
    </React.Fragment>
  );
};

export default FinancialStatement;
